import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { Box, Tooltip, ListItemText, Chip } from '@mui/material';
// hooks
import useActiveLink from '../../../hooks/useActiveLink';
// locales
import { useLocales } from '../../../locales';
// auth
import RoleBasedGuard from '../../../auth/RoleBasedGuard';
// utils
import { hasOwnPage } from '../../../utils/navigation';
//
import Iconify from '../../iconify';
//
import { StyledItem, StyledIcon } from './styles';

// ----------------------------------------------------------------------

NavItem.propTypes = {
  open: PropTypes.bool,
  active: PropTypes.bool,
  item: PropTypes.object,
  depth: PropTypes.number,
  isExternalLink: PropTypes.bool,
};

export default function NavItem({ item, depth, open, active, isExternalLink, ...other }) {
  const { translate } = useLocales();

  const {
    title,
    path,
    icon,
    info,
    children,
    disabled,
    locked,
    caption,
    roles,
    tag,
    hideInfoWhenOpen,
    onClick: onItemClick,
  } = item;

  // `active` compara por prefixo, entao o pai de um submenu acende tambem na pagina de um
  // filho. Pai so e a pagina atual no path exato; senao pai e filho anunciam "pagina atual".
  const { active: isExactPath } = useActiveLink(path, false);
  const isCurrentPage = children ? isExactPath : active;

  // Item que carrega contador e tem filho tambem contador: com o submenu aberto os dois
  // mostram o mesmo numero lado a lado. Aberto, quem responde e o filho. Exige `children`
  // porque numa folha `open` acompanha o item ativo, e a flag apagaria o contador a esmo.
  const showInfo = !!info && !(hideInfoWhenOpen && open && !!children);

  const renderStyledItem = (extraProps = {}) => (
    <StyledItem
      depth={depth}
      active={active}
      disabled={disabled}
      locked={locked}
      aria-disabled={locked || undefined}
      caption={!!caption}
      {...other}
      {...extraProps}
    >
      {icon && <StyledIcon>{icon}</StyledIcon>}

      <ListItemText
        primary={`${translate(title)}`}
        secondary={
          caption && (
            <Tooltip title={`${translate(caption)}`} placement="top-start">
              <span>{`${translate(caption)}`}</span>
            </Tooltip>
          )
        }
        primaryTypographyProps={{
          noWrap: true,
          component: 'span',
          variant: active ? 'subtitle2' : 'body2',
        }}
        secondaryTypographyProps={{
          noWrap: true,
          variant: 'caption',
        }}
      />

      {showInfo && (
        <Box component="span" sx={{ lineHeight: 0 }}>
          {info}
        </Box>
      )}

      {tag && <Chip size="small" color={tag.color} label={tag.label} variant="outlined" />}

      {/* Item desabilitado ou travado nao abre submenu, entao a seta so empurrava o
          cadeado pra fora da coluna dos outros itens. */}
      {!!children && !disabled && !locked && (
        <Iconify
          width={16}
          icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
          sx={{ ml: 1, flexShrink: 0 }}
        />
      )}
    </StyledItem>
  );

  const renderItem = () => {
    if (onItemClick) {
      return renderStyledItem({ onClick: onItemClick });
    }

    // O item e o proprio link: envolto num <a>, o botao interno virava uma segunda parada de Tab.
    if (isExternalLink) {
      return renderStyledItem({ component: 'a', href: path, target: '_blank', rel: 'noopener' });
    }

    if (children && !hasOwnPage(path)) {
      return renderStyledItem();
    }

    return renderStyledItem({
      component: RouterLink,
      to: path,
      'aria-current': isCurrentPage ? 'page' : undefined,
    });
  };

  return <RoleBasedGuard roles={roles}> {renderItem()} </RoleBasedGuard>;
}
